import { FC, ReactNode, useCallback, useRef } from "react";
import { Platform, ScrollView, StyleProp, StyleSheet, Text, TextStyle, TouchableOpacity, View } from "react-native";
import { kColors } from "../../kColors";
import { useEventListener } from "usehooks-ts";
import { StandardButton } from "..";
import { kStyleConst } from "../../kStyle";
import { CancelIcon } from "../impComponents";
import { tButtonTypes } from "../StandardButton";
import { kFonts } from "../../kFonts";
import { useDisplayErrorRibbon } from "../../redux/errorHandler/actions";

export type tModalBodyItem = {
  content: string;
  style?: StyleProp<TextStyle>;
};

export type tModalButton = {
  id: string;
  label: string;
  onPress: () => void;
  isPrimaryAction?: boolean;
  type?: tButtonTypes;
  disabled?: boolean;
};

type tModalProps = {
  title: string;
  subtitle?: string;
  body?: tModalBodyItem | tModalBodyItem[];
  children?: ReactNode;
  buttons: Array<tModalButton>;
  onCancel: () => void;
  isLoading?: boolean;
};

const Modal: FC<tModalProps> = ({ title, subtitle, isLoading, body, children, buttons, onCancel }) => {
  useEventListener("keydown", (event) => {
    if (Platform.OS === "web") {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const kKeyboardEvent = event as KeyboardEvent;
      if (kKeyboardEvent.key === "Escape") {
        onCancel();
      } else if (kKeyboardEvent.key === "Enter") {
        buttons.forEach((myButton) => {
          if (myButton.isPrimaryAction) myButton.onPress();
        });
      }
    }
  });
  useDisplayErrorRibbon();
  const kModalRef = useRef<View>(null);
  useEventListener("click", (event) => {
    if (Platform.OS === "web") {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const kPointerEvent = event as PointerEvent;
      kModalRef.current?.measure((_x, _y, width, height, pageX, pageY) => {
        const isInXRegion = kPointerEvent.pageX > pageX && kPointerEvent.pageX < pageX + width;
        const isInYRegion = kPointerEvent.pageY > pageY && kPointerEvent.pageY < pageY + height;

        if (!(isInXRegion && isInYRegion)) {
          onCancel();
        }
      });
    }
  });
  return (
    <>
      <View style={styles.modalContainer}>
        <View ref={kModalRef} style={styles.modal}>
          <View style={styles.modalHeader}>
            <View style={styles.between}>
              <Text style={styles.modalHeaderText}>{title}</Text>
              <TouchableOpacity style={styles.closeModalIcon} onPress={useCallback(() => onCancel(), [onCancel])}>
                <CancelIcon />
              </TouchableOpacity>
            </View>
            <Text style={styles.subtitle}>{subtitle}</Text>
          </View>
          <ScrollView contentContainerStyle={styles.flexOne} style={styles.flexOne}>
            {Array.isArray(body) ? (
              <View style={styles.flexOne}>
                {body.map((aBody, myIndex) => (
                  <Text key={`${aBody}${myIndex.toString()}`} style={aBody.style}>
                    {aBody.content}
                  </Text>
                ))}
              </View>
            ) : (
              <View style={styles.flexOne}>
                <Text style={body?.style}>{body?.content}</Text>
              </View>
            )}
            {children}
          </ScrollView>
          <View style={styles.modalButtons}>
            {buttons.map((myButton, index) => (
              <StandardButton
                key={myButton.id}
                onPress={myButton.onPress}
                type={myButton.type ? myButton.type : myButton.isPrimaryAction ? "primary" : "secondary"}
                text={myButton.label}
                disabled={myButton.disabled}
                isLoading={isLoading}
                style={[
                  !myButton.type
                    ? myButton.isPrimaryAction
                      ? styles.modalPrimaryButton
                      : styles.modalSecondaryButton
                    : undefined,
                  buttons.length - 1 > index ? styles.mr4 : {},
                ]}
              />
            ))}
          </View>
        </View>
      </View>
    </>
  );
};

export default Modal;

const styles = StyleSheet.create({
  between: {
    alignItems: "flex-start",
    flexDirection: "row",
  },
  closeModalIcon: {
    ...kStyleConst.kPlusMinusButton,
    opacity: 0.75,
  },
  flexOne: {
    flex: 1,
  },
  modal: {
    backgroundColor: kColors.white,
    flexShrink: 1,
    maxWidth: "90%",
    paddingHorizontal: kStyleConst.kSpacing * 5,
    paddingVertical: kStyleConst.kSpacing * 5,
    width: 600,
  },
  modalButtons: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 50,
  },
  modalContainer: {
    alignItems: "center",
    backgroundColor: kColors.transparentBlack,
    bottom: 0,
    flex: 1,
    justifyContent: "center",
    left: 0,
    paddingVertical: kStyleConst.kSpacing * 5,
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: 150,
  },
  modalHeader: {
    marginBottom: kStyleConst.kSpacing * 2,
  },
  modalHeaderText: {
    flex: 1,
    fontSize: 22,
    fontWeight: "normal",
    marginBottom: kStyleConst.kSpacing * 2,
  },
  modalPrimaryButton: {
    backgroundColor: kColors.brandGreen,
    borderRadius: kStyleConst.kBorderRadius,
    borderWidth: 0,
    height: 40,
    width: 150,
  },
  modalSecondaryButton: {
    borderColor: kColors.brandDarkGreen,
    borderRadius: kStyleConst.kBorderRadius,
    borderWidth: 1,
    height: 40,
    width: 150,
  },
  mr4: {
    marginRight: kStyleConst.kSpacing * 5,
  },
  subtitle: {
    ...kFonts.body,
  },
});
